import moment from 'moment';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { BodyXS } from '../../styles/Typography';

type CountdownTimerProps = {
  /** Counts down from this value of seconds */
  initSeconds: number;
  message: string;
  onZero?: () => void;
  className?: string;
};

/** A card with a timer counting down from `initSeconds` to zero */
export const CountdownTimer: FunctionComponent<CountdownTimerProps> = (props) => {
  const secondsRemaining = useCountdownTimer(props.initSeconds);

  useEffect(
    function handleEndOfTimer() {
      if (secondsRemaining === 0) {
        props.onZero?.();
      }
    },
    [secondsRemaining]
  );

  return (
    <TimerContainer className={props.className} data-test="countdownTimer">
      <Paragraph>{props.message}</Paragraph>
      <TimerLabel>Time remaining:</TimerLabel>
      <Timer>{formatSecondsRemainingAsTime(secondsRemaining)}</Timer>
    </TimerContainer>
  );
};

function useCountdownTimer(initSeconds: number): number {
  const [secondsRemaining, setSecondsRemaining] = useState(initSeconds);

  useEffect(function subtract1SecondEvery1SecondTillZero() {
    const interval = setInterval(() => {
      setSecondsRemaining((prevSeconds) => {
        const seconds = prevSeconds - 1;
        if (seconds === 0) {
          clearInterval(interval);
        }
        return seconds;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return secondsRemaining;
}

/** Formats seconds to a time string e.g. `325` secs becomes `'5:25'` */
function formatSecondsRemainingAsTime(seconds: number) {
  return moment.utc(seconds * 1000).format('mm:ss');
}

const TimerContainer = styled.div`
  margin: 24px -16px;
  background-color: ${appOverride({
    nerva: 'rgba(255, 255, 255, 0.1)',
    default: Color.subscription.surface,
  })};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing._16px};
  text-align: center;
  color: ${Color.content.default};
`;

const Paragraph = styled(BodyXS)`
  margin: ${({ theme }) => `0 0 ${theme.spacing._12px}`};
`;

const TimerLabel = styled(BodyXS)`
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const Timer = styled(BodyXS)`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
